import { useTranslation } from 'react-i18next';
import type { ChannexQuarantinedRevision } from '@/shared/api';
import { Pill } from '@/shared/components';
import { OtaLogo } from '@/features/properties/OtaLogo';
import { cn } from '@/shared/lib/cn';
import { QUARANTINE_REASON, holdReason } from './quarantineLabels';
import { holdStateLabel } from './revisionHoldPresentation';
import { RevisionHoldReplayAction } from './RevisionHoldReplayAction';
import { RevisionHoldMenu } from './RevisionHoldMenu';
import { waitText, attemptsText, title, facts } from './revisionHoldRowText';
import type { ReplayRowState } from './useRevisionReplay';

interface Props {
  row: ChannexQuarantinedRevision;
  /** Der Server versucht wartende Revisionen selbst. */
  automaticRetry: boolean;
  replayState: ReplayRowState | undefined;
  onReplay: () => void;
  /** Fehlt ohne das Recht dazu; dann gibt es das Menü nicht. */
  onReleaseNights?: (trigger: HTMLElement | null) => void;
  onRemove?: (trigger: HTMLElement | null) => void;
}

/**
 * Eine Kanalbuchung ohne Buchung als Arbeitszeile: woran man sie beim Kanal findet, ihr Zustand,
 * der Grund im Klartext und darunter höchstens eine nächste Aktion. Die Freigabe der Nächte liegt
 * im Menü — sie ist das Gegenteil der Wiedervorlage und braucht eine eigene Bestätigung. Der
 * Kopf trägt Kanal, Nummer, Zustand und Menü, damit der Text darunter die volle Breite hat.
 */
export function RevisionHoldRow({
  row,
  automaticRetry,
  replayState,
  onReplay,
  onReleaseNights,
  onRemove,
}: Props) {
  const { t } = useTranslation('revisionHolds');
  const waiting = row.processingState === 'AwaitingRoute';
  const outcome = replayState?.kind === 'done' ? replayState.result.outcome : null;
  const resolved = outcome === 'Applied' || outcome === 'Superseded';
  return (
    <li
      className={cn(
        'bg-card border-l-3 px-4 py-3.5',
        resolved ? 'border-l-sage' : waiting ? 'border-l-amber' : 'border-l-sealwax',
      )}
    >
      <div className="flex items-start gap-2">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
          {row.details && <OtaLogo channel={row.details.channel} size={20} />}
          <h3 className="text-ink wrap-break-word min-w-0 font-serif text-[17px]">{title(row)}</h3>
          {resolved ? (
            <Pill variant={outcome === 'Applied' ? 'live' : 'default'} size="sm">
              {outcome === 'Applied' ? 'Übernommen' : 'Überholt'}
            </Pill>
          ) : (
            <Pill variant={waiting ? 'warning' : 'stop'} size="sm">
              {holdStateLabel(row.processingState)}
            </Pill>
          )}
        </div>
        {!waiting && !resolved && (onReleaseNights || onRemove) && (
          <RevisionHoldMenu onRederive={onReleaseNights} onRemove={onRemove} />
        )}
      </div>
      <p className="text-ink-soft mt-1 text-[13px]">{facts(row).join(' · ')}</p>
      {!resolved && (
        <p className="text-ink-soft mt-1 text-sm">
          {t(row.hasExistingBooking ? 'existingChange' : 'missingBooking')}
        </p>
      )}
      <p className="text-ink mt-1.5 text-sm">{holdReason(row.quarantineCode, row)}</p>
      {/* Der Rohcode nur zusätzlich zur Erklärung — bei einem unbekannten Code IST er bereits
          die Erklärung und stünde sonst zweimal da. */}
      {row.quarantineCode && QUARANTINE_REASON[row.quarantineCode] && (
        <p className="text-ink-faint mt-0.5 font-mono text-[11px]">{row.quarantineCode}</p>
      )}
      {waiting && !resolved && (
        <p className="text-ink-soft mt-1 text-[13px]">{waitText(row, automaticRetry)}</p>
      )}
      {row.replayAttempts > 0 && row.lastReplayError && (
        <p className="text-ink-faint mt-1 text-[12px]">{attemptsText(row)}</p>
      )}
      {!waiting && <RevisionHoldReplayAction row={row} state={replayState} onReplay={onReplay} />}
    </li>
  );
}
